import {
  IStateMetaData,
  IMachineContext,
  TKickoffStates,
  TKickoffEvents,
  TMachineActions,
  TInputTypes,
  TMachineStates,
  TMachineEvents,
  IYardageSelectionEvent,
  TRefEventStates,
  TRefEventsActions,
  TWorkflowType,
  TCoinTossStates,
  TPlayResults,
} from '@/types';
import type { AnyEventObject, BaseActionObject, StateNodeConfig } from 'xstate';
import { MAIN_MACHINE_ID, TPlayResultTypes, YARDAGE_SELECTOR_BUTTONS } from '@/constants';
import { handleYardageToggle, CONFIRM_BASIC_META, HANDLE_YARDAGE_TOGGLES_META } from '../utils';
import { KICKOFF_REF_WF } from './refEventFlows/kickoff';
import { last } from 'lodash';
import { onKickoffKicked, onKickoffResult, onKickoffStarted } from './nextActions/kickoff';

export const KICKOFF_MACHINE: StateNodeConfig<IMachineContext, any, AnyEventObject, BaseActionObject> = {
  initial: TKickoffStates.START_KICKOFF,
  states: {
    [TKickoffStates.START_KICKOFF]: {
      meta: {
        component: TInputTypes.TWO_ROW_GENERIC,
        componentConfiguration: {
          title: 'Kickoff',
          showLogo: true,
          showBackBtn: true,
        },
        onNext: onKickoffStarted,
        onBack({ send, context }) {
          const backEvt = context.inputForNextComponent.backTo;
          if (!backEvt) {
            return;
          }

          let currentWorkflow = null;
          if (backEvt.includes(TWorkflowType.COIN)) {
            currentWorkflow = TWorkflowType.COIN;
          }

          send(context.inputForNextComponent.backTo, {
            ctxUpdate: {
              currentWorkflow,
            },
          });
        },
      } as IStateMetaData,
      on: {
        [TKickoffEvents.REGULAR_KICK]: {
          target: TKickoffStates.KICKOFF_KICKED,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TKickoffEvents.REF_EVENT]: {
          target: TKickoffStates.REF_EVENT,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TKickoffEvents.TO_COIN_TOSS_SELECT_SIDE]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_COIN}.${TCoinTossStates.SELECT_SIDE}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TKickoffEvents.TO_IDEL]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.IDLE}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
      },
    },

    [TKickoffStates.KICKOFF_KICKED]: {
      meta: {
        component: TInputTypes.TWO_ROW_GENERIC,
        componentConfiguration: {
          title: 'Kickoff',
          showLogo: true,
          showBackBtn: true,
        },
        onNext: onKickoffKicked,
        onBack({ send }) {
          send(TKickoffEvents.BACK, {});
        },
      } as IStateMetaData,
      on: {
        [TKickoffEvents.REGULAR_KICK]: {
          target: TKickoffStates.KICKOFF_RESULT,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TKickoffEvents.ONSIDE_KICK]: {
          target: TKickoffStates.KICKOFF_RESULT,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TKickoffEvents.REF_EVENT]: {
          target: TKickoffStates.REF_EVENT,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TKickoffEvents.BACK]: {
          target: TKickoffStates.START_KICKOFF,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
      },
    },

    [TKickoffStates.KICKOFF_RESULT]: {
      meta: {
        component: TInputTypes.TWO_ROW_GENERIC,
        componentConfiguration: {
          title: 'Kickoff',
          showLogo: true,
          showBackBtn: true,
        },
        onNext: onKickoffResult,
        onBack({ send }) {
          send(TKickoffEvents.BACK);
        },
      } as IStateMetaData,
      on: {
        [TKickoffEvents.KICK_RESULT]: {
          target: TKickoffStates.SELECT_YARDAGE,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TKickoffEvents.BACK]: {
          target: TKickoffStates.KICKOFF_KICKED,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TKickoffEvents.PENALTY]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_KICKOFF}.${TKickoffStates.REF_EVENT}.${TRefEventStates.PENALTY}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TKickoffEvents.KICKOFF_COMPLETED]: {
          target: TKickoffStates.KICKOFF_CONFIRMATION,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
      },
    },

    [TKickoffStates.SELECT_YARDAGE]: {
      meta: {
        component: TInputTypes.YARDAGE_SELECTOR,
        componentConfiguration: {
          title: 'Kickoff',
          showLogo: true,
          showBackBtn: true,
        },
        onNext(inp) {
          const { context, payload } = inp;
          const resultType = !last(context.currentPlay.resultType) ? TPlayResultTypes.FIRST_DOWN : null;

          handleYardageToggle(inp, TMachineEvents.BACK_TO_YARDAGE_SELECTION, {
            side: (payload as IYardageSelectionEvent).side,
            yardLine: (payload as IYardageSelectionEvent).yardLine,
            resultType,
            currToggle: 0,
          });
        },
        onBack({ send, context }) {
          send(context.inputForNextComponent.backTo, {});
        },
      } as IStateMetaData,
      on: {
        [TMachineEvents.PLAY_COMPLETED]: {
          target: TKickoffStates.KICKOFF_CONFIRMATION,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TMachineEvents.BACK]: {
          target: TKickoffStates.KICKOFF_RESULT,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.BACK_TO_UNDER_REVIEW]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_KICKOFF}.${TKickoffStates.REF_EVENT}.${TRefEventStates.UNDER_REVIEW}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.BACK_TO_CHAIN]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_KICKOFF}.${TKickoffStates.REF_EVENT}.${TRefEventStates.CHAINS}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.BACK_TO_PENALTY]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_KICKOFF}.${TKickoffStates.REF_EVENT}.${TRefEventStates.PENALTY}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.BACK_TO_REF]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_KICKOFF}.${TKickoffStates.REF_EVENT}.${TRefEventStates.SELECT_REF_TYPE}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TMachineEvents.TO_HANDLE_YARDAGE_TOGGLE]: {
          target: TKickoffStates.HANDLE_YARDAGE_TOGGLES,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TMachineEvents.BACK_TO_HANDLE_YARDAGE_TOGGLE]: {
          target: TKickoffStates.HANDLE_YARDAGE_TOGGLES,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TKickoffEvents.BACK_TO_SELECT_POSSESSION]: {
          target: TKickoffStates.SELECT_POSSESSION,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
      },
    },

    [TKickoffStates.REF_EVENT]: KICKOFF_REF_WF,

    [TKickoffStates.HANDLE_YARDAGE_TOGGLES]: {
      meta: HANDLE_YARDAGE_TOGGLES_META,
      on: {
        [TMachineEvents.BACK_TO_YARDAGE_SELECTION]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_KICKOFF}.${TKickoffStates.SELECT_YARDAGE}`,
          actions: [
            TMachineActions.UPDATE_CONTEX,
            TMachineActions.UNSET_NEXT_STATE_INPUT,
            TMachineActions.CLEAR_YARDAGE_TOGGLE_DATA,
          ],
        },
        [TMachineEvents.BACK_TO_SAME]: {
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TMachineEvents.TO_HANDLE_YARDAGE_TOGGLE]: {
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TMachineEvents.POST_PENALTY_YARDAGE]: {
          target: TKickoffStates.SELECT_YARDAGE,
          actions: [
            TMachineActions.UPDATE_CONTEX,
            TMachineActions.SET_NEXT_STATE_INPUT,
            TMachineActions.CLEAR_YARDAGE_TOGGLE_DATA,
          ],
        },
        [TMachineEvents.PLAY_COMPLETED]: {
          target: TKickoffStates.KICKOFF_CONFIRMATION,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
      },
    },

    [TKickoffStates.KICKOFF_CONFIRMATION]: {
      meta: CONFIRM_BASIC_META,
      on: {
        [TMachineEvents.CONFIRMED]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.IDLE}`,
          actions: [
            TMachineActions.UPDATE_CONTEX,
            TMachineActions.SET_NEXT_STATE_INPUT,
            TMachineActions.SAVE_PLAY,
            TMachineActions.CLEAR_YARDAGE_TOGGLE_DATA,
          ],
        },
        [TKickoffEvents.BACK_TO_KICKOFF_START]: {
          target: TKickoffStates.START_KICKOFF,
          actions: [
            TMachineActions.UPDATE_CONTEX,
            TMachineActions.SET_NEXT_STATE_INPUT,
            TMachineActions.SAVE_PLAY,
            TMachineActions.CLEAR_YARDAGE_TOGGLE_DATA,
          ],
        },
        [TMachineEvents.CONFIRM_WITH_SAFTEY]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_KICKOFF}`,
          actions: [
            TMachineActions.UPDATE_CONTEX,
            TMachineActions.SET_NEXT_STATE_INPUT,
            TMachineActions.SAVE_PLAY,
            TMachineActions.CLEAR_YARDAGE_TOGGLE_DATA,
          ],
        },
        [TMachineEvents.TO_PAT]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.PAT}`,
          actions: [
            TMachineActions.UPDATE_CONTEX,
            TMachineActions.SET_NEXT_STATE_INPUT,
            TMachineActions.SAVE_PLAY,
            TMachineActions.CLEAR_YARDAGE_TOGGLE_DATA,
          ],
        },
        [TMachineEvents.ON_REF_EVENT]: {
          target: TKickoffStates.REF_EVENT,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
        [TMachineEvents.BACK_TO_YARDAGE_SELECTION]: {
          target: TKickoffStates.SELECT_YARDAGE,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TMachineEvents.BACK_TO_HANDLE_YARDAGE]: {
          target: TKickoffStates.HANDLE_YARDAGE_TOGGLES,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TRefEventsActions.BACK_TO_KICKOFF_RESULT]: {
          target: TKickoffStates.KICKOFF_RESULT,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
      },
    },

    [TKickoffStates.SELECT_POSSESSION]: {
      meta: {
        component: TInputTypes.TWO_ROW_GENERIC,
        componentConfiguration: {
          title: 'Select Possession',
          showLogo: true,
          showBackBtn: true,
        },
        onNext: ({ send, generalState, playFlowState, payload, context }) => {
          const ctxUpdate: Partial<IMachineContext> = {
            currentPlay: {
              ...context.currentPlay,
              resultType: context.currentPlay?.resultType || [],
              options: {
                ...context.currentPlay.options,
              },
            },
          };

          if (payload.value === playFlowState.activeTeam) {
            ctxUpdate.currentPlay.options.isPenaltyDuringReturn = true;
            ctxUpdate.currentPlay.options.isPossessionRetained = true;
            ctxUpdate.currentPlay.options.isReplayDown = true;
          }

          return send(TKickoffEvents.POSSESSION_SELECTED, {
            ctxUpdate,
            nextStateInput: {
              buttons: YARDAGE_SELECTOR_BUTTONS(),
              sliderLabels: {
                left: generalState.teams[playFlowState.teams.left].abbreviation,
                right: generalState.teams[playFlowState.teams.right].abbreviation,
              },
              backTo: TKickoffEvents.BACK_TO_SELECT_POSSESSION,
            },
          });
        },
        onBack({ send, context }) {
          send(context.inputForNextComponent.backTo, {});
        },
      } as IStateMetaData,
      on: {
        [TRefEventsActions.BACK_TO_PENALTY]: {
          target: `#${MAIN_MACHINE_ID}.${TMachineStates.INIT_KICKOFF}.${TKickoffStates.REF_EVENT}.${TRefEventStates.PENALTY}`,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TKickoffEvents.BACK_TO_SELECT_POSSESSION]: {
          target: TKickoffStates.SELECT_POSSESSION,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.UNSET_NEXT_STATE_INPUT],
        },
        [TKickoffEvents.POSSESSION_SELECTED]: {
          target: TKickoffStates.SELECT_YARDAGE,
          actions: [TMachineActions.UPDATE_CONTEX, TMachineActions.SET_NEXT_STATE_INPUT],
        },
      },
    },
  },
};
